<template lang="pug">
  div#app-shell
    router-view

    // Snackbar (popup notifications)
    snackbar-component
</template>

<script>
    import { App as CapacitorApp } from '@capacitor/app';
    import { StatusBar, Style } from '@capacitor/status-bar';
    import { SplashScreen } from '@capacitor/splash-screen';
    import { Keyboard, KeyboardResize } from '@capacitor/keyboard';
    import Snackbar from '~components/Snackbar';
    import {
        PushNotificationsNative,
        LiveUpdates,
        LiveActivity,
        isNative,
        isIOS,
        isAndroid
    } from '~services';

    export default {
        components: {
            SnackbarComponent: Snackbar
        },
        async mounted() {
            if (!isNative()) return;

            // --- Status bar (match brand navy) ---
            try {
                await StatusBar.setStyle({ style: Style.Dark });
                if (isAndroid()) {
                    await StatusBar.setBackgroundColor({ color: '#022341' });
                }
                await StatusBar.setOverlaysWebView({ overlay: false });
            } catch (e) {
                /* non-fatal: device without a status bar */
            }

            // --- Keyboard resize mode (iOS) ---
            if (isIOS()) {
                try {
                    await Keyboard.setResizeMode({
                        mode: KeyboardResize.Body
                    });
                } catch (_) {
                    /* ignore */
                }
            }

            // --- Push notifications bootstrap ---
            PushNotificationsNative.initPushnotifications({
                store: this.$store
            }).catch(err => console.warn('[app] push init failed', err));

            // --- Capgo live updates ---
            LiveUpdates.init();

            // Boot-time sweep: end any orphaned Live Activity left over
            // from a crash/force-quit so the user never sees a stale
            // countdown on the Lock Screen.
            LiveActivity.cleanup();

            // --- Android hardware back-button behaviour ---
            if (isAndroid()) {
                CapacitorApp.addListener('backButton', ({ canGoBack }) => {
                    if (!canGoBack) {
                        CapacitorApp.exitApp();
                    } else {
                        this.$router.back();
                    }
                });
            }

            // --- Universal / App Links (appUrlOpen) ---
            CapacitorApp.addListener('appUrlOpen', ({ url }) => {
                try {
                    const parsed = new URL(url);
                    const path =
                        parsed.pathname +
                        (parsed.search || '') +
                        (parsed.hash || '');
                    if (path && path !== this.$route.fullPath) {
                        this.$router.push(path).catch(() => {});
                    }
                } catch (_) {
                    /* malformed URL — ignore */
                }
            });

            // --- Dismiss the splash once Vue has mounted ---
            try {
                await SplashScreen.hide({ fadeOutDuration: 200 });
            } catch (_) {
                /* splash already gone */
            }
        }
    };
</script>

<style lang="stylus">
    @import './assets/style/_variables.styl'

    // Every outer container is painted brand navy so any momentary gap
    // between Vuetify's v-app and the native edges (status bar area on
    // iOS, home indicator area at the bottom) blends in instead of
    // showing the default white body.
    html, body, #app, #app-shell
        height 100%
        background #022341

    #app-shell
        // Edge-to-edge is mandatory on Android 15+ (targetSdk 36) and
        // intentional on iOS (contentInset: 'never'), so the WebView
        // draws under both system bars. iOS reports 0 for the top inset
        // because StatusBar.overlaysWebView=false already insets the
        // bridge; Android reports the actual status-bar + cutout +
        // gesture-nav heights so the app chrome never sits behind the
        // system clock or the gesture pill / 3-button nav.
        // position:fixed elements bypass these paddings by design and
        // must apply env(safe-area-inset-*) themselves; viewport-unit
        // (100vh) elements likewise need calc() to subtract the insets.
        padding-top env(safe-area-inset-top, 0px)
        padding-bottom env(safe-area-inset-bottom, 0px)
        padding-left env(safe-area-inset-left)
        padding-right env(safe-area-inset-right)
        box-sizing border-box
</style>
